/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

// NOTE: This entity display does not support Tabular Data at this time.
class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	getSelectInputHTMLID(pInputHTMLID)
	{
		return `#LINEITEM-SELECT-FOR-${pInputHTMLID}`;
	}

	getTabularSelectInputID(pInputHTMLID, pRowIndex)
	{
		return `#LINEITEM-SELECT-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularSelectDropdownID(pInputHTMLID, pRowIndex)
	{
		return `#LINEITEM-SELECT-TABULAR-DROPDOWN-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Temporary loading skeleton for the input element.
	 * @param {object} pInput The input object.
	 */
	addLoadingSkeleton(pInput)
	{
		if (!this.pict?.AppData?.Interactive || this.pict?.AppData?.DocumentData?.ReportData?.Locked)
		{
			return;
		}
		const tmpLoadingBox = document.createElement('div');
		tmpLoadingBox.id = `LOADING-${pInput.Macro.RawHTMLID}`;
		tmpLoadingBox.classList.add('input', 'is-skeleton');
		tmpLoadingBox.style.left = '0';
		tmpLoadingBox.style.position = 'absolute';
		tmpLoadingBox.style.opacity = '0.5';
		this.pict.ContentAssignment.getElement(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID))[0]?.parentElement?.appendChild(tmpLoadingBox);
		this.pict.ContentAssignment.addClass(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID), 'is-hidden');
	}

	/**
	 * Remove the loading skeleton from the input element.
	 * @param {object} pInput The input object.
	 */
	removeLoadingSkeleton(pInput)
	{
		const tmpLoadingBox = this.pict.ContentAssignment.getElement(`#LOADING-${pInput.Macro.RawHTMLID}`);
		if (tmpLoadingBox && tmpLoadingBox.length > 0)
		{
			tmpLoadingBox[0].remove();
		}
		this.pict.ContentAssignment.removeClass(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID), 'is-hidden');
	}

	/**
	 * Initialize the Select2 plugin for the input element.
	 * @param {object} pInput The input object.
	 */
	initializeSelect2(pInput)
	{
		if (window['jQuery'] && window['jQuery'].fn.select2)
		{
			const tmpSelectOptions = this.pict.providers.Select2Util.getOptionsAjax('LineItem',
				{
					Input: pInput,
					SearchFields: [ 'ItemCode', 'Name' ],
					SearchFallbackPlaceholder: 'Select a Line Item by name or code...',
					GetRecordUserFacingID: (pLineItem) => pLineItem.ItemCode || 'N/A',
					HandleSearchFilters: (pFilters) =>
					{
						// always ensure we have a sort by Name ascending
						if (!pFilters.length || !pFilters.includes('FSF'))
						{
							pFilters.push('FSF~Name~ASC~0');
						}
						return pFilters;
					},
				});
			// if the select2 plugin is not already initialized, initialize it now for the correct input type
			if (!window['jQuery'](this.getSelectInputHTMLID(pInput.Macro.RawHTMLID)).data('select2'))
			{
				window['jQuery'](this.getSelectInputHTMLID(pInput.Macro.RawHTMLID)).select2(tmpSelectOptions);
			}
		}
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.addLoadingSkeleton(pInput);
		// initialize the select2 plugin
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		//FIXME: base class doesn't allow this to be async - changed to run async safely for now, but not sure if this creates timing problems
		this._onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector).catch((pError) =>
		{
			this.pict.log.error(`Error in onDataMarshalToForm: ${pError}`);
		});
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @return {Promise<boolean>}
	 */
	async _onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector)
	{
		const tmpInputSelectElements = this.pict.ContentAssignment.getElement(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
		if (!tmpInputSelectElements || tmpInputSelectElements.length < 1)
		{
			return false;
		}
		// if we have a value (ID), go fetch the LineItem for display (Name, etc.)
		if (pValue)
		{
			// get the value of the selected LineItem and set it as the selected LineItem on the data object
			const tmpLineItem = await this.pict.providers.LineItemProvider.getLineItemByID(pValue);
			this.pict.AppData.SelectedLineItem = tmpLineItem;
			// build the prefilled value for the select2 dropdown
			const tmpOption = document.createElement('option');
			const tmpSelectElement = this.pict.ContentAssignment.getElement(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID))[0];
			tmpOption.value = this.pict.AppData.SelectedLineItem.IDLineItem;
			tmpOption.text = `${this.pict.AppData.SelectedLineItem.ItemCode || 'N/A'}|~|${this.pict.AppData.SelectedLineItem.Name || ('Unnamed LineItem #' + this.pict.AppData.SelectedLineItem.IDLineItem)}`;
			tmpOption.selected = true;
			tmpSelectElement.appendChild(tmpOption);
		}
		this.removeLoadingSkeleton(pInput);
		this.initializeSelect2(pInput);
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		const tmpInputSelectValue = this.pict.ContentAssignment.readContent(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID));
		this.pict.ContentAssignment.assignContent(pHTMLSelector, tmpInputSelectValue);
		pView.dataChanged(pInput.Hash);
		return super.onDataRequest(pView, pInput, tmpInputSelectValue, pHTMLSelector);
	}
}

module.exports = CustomInputHandler;
